// src/common/context/CsvExportContext.jsx
// One export button lives in the CustomerInfo header. Each tab tells it what
// to export by calling useRegisterCsvExport(). Only one tab is mounted at a
// time, so the "active" registration is always the visible tab.
//
// Setter and value are deliberately split into two contexts: the setter is
// stable, so registering does not re-trigger the registering effect.

import React, { createContext, useContext, useEffect, useId, useMemo, useState } from "react";

const CsvExportSetterContext = createContext(null);
const CsvExportTargetContext = createContext(null);

export function CsvExportProvider({ filePrefix = "export", children }) {
    const [target, setTarget] = useState(null);

    const value = useMemo(
        () => (target ? { ...target, filePrefix } : null),
        [target, filePrefix]
    );

    return (
        <CsvExportSetterContext.Provider value={setTarget}>
            <CsvExportTargetContext.Provider value={value}>
                {children}
            </CsvExportTargetContext.Provider>
        </CsvExportSetterContext.Provider>
    );
}

export const useCsvExportTarget = () => useContext(CsvExportTargetContext);

/**
 * Register the current tab's data with the shared Export CSV button.
 *
 * @param {string}   name     filename segment, e.g. "notes", "payments"
 * @param {Array}    columns  column config — MEMOIZE THIS (useMemo) in the tab
 * @param {Array}    rows     the rows to export (usually the same state the table renders)
 * @param {boolean} [enabled] set false while loading / when the tab has nothing to give
 */
export function useRegisterCsvExport({ name, columns, rows, enabled = true }) {
    const setTarget = useContext(CsvExportSetterContext);
    const id = useId();

    useEffect(() => {
        // No provider (tab rendered standalone) — quietly do nothing.
        if (!setTarget) return undefined;

        if (!enabled) {
            setTarget((t) => (t && t.id === id ? null : t));
            return undefined;
        }

        setTarget({ id, name, columns, rows });

        // Only clear if we're still the active registration.
        return () => setTarget((t) => (t && t.id === id ? null : t));
    }, [setTarget, id, name, columns, rows, enabled]);
}